// Vendor
import * as tus from 'tus-js-client'

// Personal libs
import { buildUrl, testApiValidity } from "./api.lib";
import { sanitizeFileName } from '@shared/lib/tools.lib';

export interface IApiUpload {
    api: string;
    isPublicApi: boolean;
    file: File;
    newFileName?: string;
    onError: (error: Error | tus.DetailedError) => void
    onProgress: (bytesUploaded: number, bytesTotal: number) => void
    onSuccess: () => void
}

export const apiUpload = async (params: IApiUpload): Promise<tus.Upload | undefined> => {
    // Check token and refresh
    if (!(await testApiValidity(params.isPublicApi))) {
        return undefined;
    }

    if (!tus.isSupported) {
        throw new Error('TUS_UPLOAD_NOT_SUPPORTED')
    }

    const path = await buildUrl(params.api)

    const options: tus.UploadOptions = {
        endpoint: path,
        uploadSize: params.file.size,
        retryDelays: [0, 3000, 6000, 12000, 24000],
        metadata: {
            filename: params.newFileName ?? sanitizeFileName(params.file.name, true),
            filetype: params.file.type,
            filesize: params.file.size.toString(),
        },
        uploadDataDuringCreation: false,
        removeFingerprintOnSuccess: true,
        onBeforeRequest: function (req: tus.HttpRequest) {
            const xhr = req.getUnderlyingObject()
            xhr.withCredentials = true
        },
        onError: function (error: Error | tus.DetailedError) {
            params.onError(error)
        },
        onProgress: function (bytesUploaded: number, bytesTotal: number) {
            params.onProgress(bytesUploaded, bytesTotal)
        },
        onSuccess: function () {
            params.onSuccess()
        }
    }

    return new tus.Upload(params.file, options)
};

export function startOrResumeUpload(upload: tus.Upload) {
    // Check if there are any previous uploads to continue
    upload.findPreviousUploads().then(function (previousUploads) {
        // Found previous uploads so we select the first one
        if (previousUploads.length) {
            upload.resumeFromPreviousUpload(previousUploads[0])
        }

        // Start the upload
        upload.start()
    })
}

export const extractExtension = (filename: string) => {
    return filename.substring(filename.lastIndexOf('.')+1)
}